import { useAdminView } from "./admin-view-provider.js";
import { MoreSubpage } from "../pages/more/more-subpage.js";
import { AdminViewRouteGate } from "./admin-view-controls.js";
import { Suspense, useEffect, useRef } from "react";
import type { ClientPageKey } from "../app/product-router.js";
import {
  ScorecardPage,
  SeasonTeeOffPage,
  SettingsPage,
  ReviewCard,
  ReviewsPage,
  StatsPage,
  CoursesPage,
  FglPage,
  RivalryHubPage,
  RivalsPage,
  RoundsPage,
  TrophiesPage,
  FeedPage,
} from "./lazy-pages.js";
import {
  ProfileCardOverlay,
  AdminCorrectionsPage,
  UsageSummaryPanel,
  AdminCommentReports,
} from "./lazy-overlays.js";
import { ClubhousePage } from "../pages/clubhouse/clubhouse-page.js";
import { openSeasonSetup } from "../pages/fgl/season-navigation.js";
import { MorePage } from "../pages/more/more-page.js";
import type { RoundListItem } from "../../shared/contracts/product-pages.js";
import type { RivalListItem } from "../../shared/contracts/product-pages.js";
import type { AppShellProps } from "./app-shell.js";

export interface PageContentProps extends AppShellProps {
  profileOpen: boolean;
  onProfileOpenChange(open: boolean): void;
  onAvatarChanged(): void;
  onAddRival(): void;
  onOpenRival(rival: RivalListItem): void;
  onOpenRound(round: RoundListItem): void;
  onOpenRoundId(id: string): void;
}

type RoutedPageProps = PageContentProps & {
  payload: NonNullable<PageContentProps["pageState"]["data"]>;
};

/**
 * Routes the current page to its view. Pages that are not yet ported to
 * the new design fall back to the existing product page views.
 */
export function PageContent(props: PageContentProps): React.JSX.Element {
  return (
    <Suspense
      fallback={
        <p className="fr-empty" role="status">
          Loading page…
        </p>
      }
    >
      <RoutedContent {...props} />
    </Suspense>
  );
}

function RoutedContent(props: PageContentProps): React.JSX.Element {
  const { workspace, route, pageState } = props;
  const view = useAdminView();
  // While the next page loads, the previous one stays on screen. More has
  // no page data of its own, so it is remembered here.
  const lastShown = useRef<ClientPageKey>(route.page);
  useEffect(() => {
    if (!pageState.pending) lastShown.current = route.page;
  });
  if (!props.authenticated) {
    return <ScorecardPage workspace={workspace} route={route} />;
  }
  if (
    (route.page === "admin" || pageState.data?.page === "admin") &&
    !view.showAdminTools &&
    route.page !== "more"
  )
    return <AdminViewRouteGate />;
  if (
    route.page === "more" ||
    (pageState.pending && lastShown.current === "more")
  ) {
    return (
      <MorePage
        role={view.showAdminTools ? "admin" : "user"}
        displayName={workspace.bootstrap?.profile.displayName}
        handicapDisplay={workspace.bootstrap?.profile.handicapDisplay}
        onOpenProfile={() => props.onProfileOpenChange(true)}
        onNavigate={route.navigate}
      />
    );
  }
  if (pageState.loading) {
    return (
      <p className="fr-empty" role="status">
        Loading page…
      </p>
    );
  }
  if (pageState.error) return <PageErrorFallback {...props} />;
  const payload = pageState.data;
  if (!payload) return <></>;
  return <PrimaryPage {...props} payload={payload} />;
}

/** Clubhouse, the rival lists, FGL, Tee Off and the scorecard. */
function PrimaryPage(props: RoutedPageProps): React.JSX.Element {
  const { workspace, route, pageState, payload } = props;
  if (payload.page === "clubhouse") {
    return (
      <ClubhousePage
        data={payload.data}
        workspace={workspace}
        route={route}
        generatedAt={payload.generatedAt}
        onAddRival={props.onAddRival}
        onOpenRound={props.onOpenRound}
        onOpenRoundId={props.onOpenRoundId}
        reload={pageState.reload}
      />
    );
  }
  if (payload.page === "rivals" && route.page === "rivalry") {
    return (
      <RivalryHubPage
        rivals={payload.data.rivals}
        generatedAt={payload.generatedAt}
        participants={workspace.bootstrap?.participants ?? []}
        selfName={workspace.bootstrap?.profile.displayName ?? ""}
        reload={pageState.reload}
        onOpenRival={props.onOpenRival}
        onAddRival={props.onAddRival}
        onOpenRoundId={props.onOpenRoundId}
        onStartRound={() => route.navigate("tee-off")}
        onOpenInsights={(id) => {
          route.navigate("rivalry");
          rememberRelation(id);
        }}
      />
    );
  }
  if (payload.page === "rivals") {
    return (
      <RivalsPage
        rivals={payload.data.rivals}
        reload={pageState.reload}
        onOpenRival={props.onOpenRival}
        onAddRival={props.onAddRival}
      />
    );
  }
  if (payload.page === "fgl") {
    return (
      <FglPage
        accountId={workspace.accountId}
        onStart={(season, slot) =>
          openSeasonSetup(season, slot, workspace.accountId, route)
        }
      />
    );
  }
  if (payload.page === "tee-off") {
    return (
      <SeasonTeeOffPage
        data={payload.data}
        workspace={workspace}
        route={route}
        reload={pageState.reload}
      />
    );
  }
  if (payload.page === "scorecard") {
    return <ScorecardPage workspace={workspace} route={route} />;
  }
  return <ActivityPage {...props} payload={payload} />;
}

/**
 * Offline or a failed page load must not block scoring: Tee Off and the
 * scorecard only need what the bootstrap and local journal already hold.
 */
function PageErrorFallback(props: PageContentProps): React.JSX.Element {
  const { workspace, route, pageState } = props;
  if (route.page === "scorecard") {
    return <ScorecardPage workspace={workspace} route={route} />;
  }
  if (route.page === "tee-off" && workspace.bootstrap) {
    return (
      <SeasonTeeOffPage
        data={{
          participants: workspace.bootstrap.participants,
          savedCourses: [],
          activeRound: workspace.snapshot,
        }}
        workspace={workspace}
        route={route}
        reload={pageState.reload}
      />
    );
  }
  return (
    <p className="fr-empty" role="alert">
      {pageState.error}
    </p>
  );
}

/** Trophies, rounds and feed; every other page falls through to SecondaryPage. */
function ActivityPage(props: RoutedPageProps): React.JSX.Element {
  const { payload, route, workspace } = props;
  switch (payload.page) {
    case "trophies":
      return (
        <TrophiesPage
          achievements={payload.data.achievements}
          onClose={() => route.navigate("clubhouse")}
        />
      );
    case "rounds":
      return (
        <RoundsPage
          rounds={payload.data.rounds}
          onOpenRound={props.onOpenRound}
          onClose={() => route.navigate("clubhouse")}
        />
      );
    case "feed":
      return (
        <FeedPage
          events={payload.data.events}
          generatedAt={payload.generatedAt}
          selfName={workspace.bootstrap?.profile.displayName ?? ""}
          onOpenRound={props.onOpenRound}
          onOpenRoundId={props.onOpenRoundId}
          onOpenRivalry={() => route.navigate("rivalry")}
          onStartRound={() => route.navigate("tee-off")}
        />
      );
    default:
      return <SecondaryPage {...props} />;
  }
}

function SecondaryPage(props: RoutedPageProps): React.JSX.Element {
  const { payload, route, workspace, pageState } = props;
  const selfName = workspace.bootstrap?.profile.displayName ?? "";
  const view = useAdminView();
  switch (payload.page) {
    case "settings":
      return (
        <SettingsPage
          accountId={workspace.accountId}
          data={payload.data}
          onOpenProfile={() => props.onProfileOpenChange(true)}
          onNavigate={route.navigate}
          onBack={() => route.navigate("more")}
        />
      );
    case "reviews":
      return <RoutedReviews {...props} />;
    case "admin":
      return (
        <MoreSubpage
          title="Admin reviews"
          onBack={() => route.navigate("more")}
        >
          <AdminCorrectionsPage />
          <UsageSummaryPanel />
          <AdminCommentReports />
          {payload.data.disputes.length === 0 ? (
            <p className="fr-lead">No disputes exist.</p>
          ) : (
            payload.data.disputes.map((item) => (
              <ReviewCard
                key={item.id}
                item={item}
                reload={pageState.reload}
                admin
              />
            ))
          )}
        </MoreSubpage>
      );
    case "stats":
      return (
        <StatsPage
          data={payload.data}
          selfName={selfName}
          onStartRound={() => route.navigate("tee-off")}
        />
      );
    case "courses":
      return (
        <CoursesPage
          courses={payload.data.courses}
          reload={pageState.reload}
          onClose={() => route.navigate("clubhouse")}
        />
      );
    case "profile":
      return (
        <ProfileCardOverlay
          open
          avatarSrc={
            payload.data.profile.hasAvatar ? "/api/v1/profile/avatar" : null
          }
          role={view.showAdminTools ? "admin" : "user"}
          onClose={() => route.navigate("clubhouse")}
          onNavigate={route.navigate}
          onSignOut={props.onSignOut}
          onSaved={pageState.reload}
          onAvatarChanged={props.onAvatarChanged}
        />
      );
    default:
      return <p className="fr-empty">This page is not available.</p>;
  }
}

import { rememberRelation } from "../pages/rivalry/comparison-selection.js";

function RoutedReviews(props: RoutedPageProps) {
  const { route, payload, pageState } = props;
  if (payload.page !== "reviews") return null;
  return (
    <MoreSubpage
      title="Results to review"
      onBack={() => route.navigate("more")}
    >
      <ReviewsPage items={payload.data.items} reload={pageState.reload} />
    </MoreSubpage>
  );
}
